import { memo, useCallback, useMemo, useState } from 'react';

import { CirclePlayIcon, CircleXIcon, ExternalLinkIcon } from '@air/design-icons';
import { FileStatusButton } from '@air/feature-file-status/ui';
import { useAirModal } from '@air/provider-modal';
import { useToasts } from '@air/provider-toast';

import { ImportCancellationModal } from '~/components/Import/ImportCancellationModal';
import { useWorkspaceImportsOpenDestination } from '~/components/v2/components/workspace/workspace-imports/useWorkspaceImportsOpenDestination';
import { WorkspaceImportsListItem } from '~/components/v2/components/workspace/workspace-imports/WorkspaceImportsListItem';
import { type WorkspaceImportRow } from '~/components/v2/components/workspace/workspace-imports/workspaceImportTypes';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { makeImporterTasksSelectorByWorkspaceId } from '~/store/tasks/selectors';
import { useResumeImportMutation } from '~/swr-hooks/importer/useResumeImportMutation';
import { useAirSelector } from '~/utils/ReduxUtils';

export type WorkspaceImportsRowProps = {
  row: WorkspaceImportRow;
};

export const WorkspaceImportsRow = memo(({ row }: WorkspaceImportsRowProps) => {
  const { openDestination } = useWorkspaceImportsOpenDestination();
  const [showImportCancellationModal] = useAirModal(ImportCancellationModal);
  const { mutation: resumeImportMutation } = useResumeImportMutation();
  const { showToast } = useToasts();
  const { currentWorkspace } = useCurrentWorkspace();
  const tasksSelector = useMemo(
    () => makeImporterTasksSelectorByWorkspaceId(currentWorkspace?.id),
    [currentWorkspace?.id],
  );
  const importerTasks = useAirSelector(tasksSelector);
  const [isResuming, setIsResuming] = useState(false);
  const isDimmed = row.kind === 'cancelled';
  const showOpen = row.kind !== 'stopped' && row.kind !== 'cancelled';
  const openLabel = row.board.id ? 'Open board' : 'Open all media';

  const handleOpen = useCallback(() => {
    openDestination(row.board);
  }, [openDestination, row.board]);

  const handleCancel = useCallback(() => {
    showImportCancellationModal({
      importId: row.id,
    });
  }, [row.id, showImportCancellationModal]);

  const handleResume = useCallback(async () => {
    const localTask = importerTasks.find((task) => task.localTaskId === row.id);

    if (!localTask?.remoteTaskId) {
      showToast('Import not found.', { color: 'red' });
      return;
    }

    setIsResuming(true);

    try {
      await resumeImportMutation.mutateAsync([localTask.remoteTaskId]);
    } catch {
      // useResumeImportMutation already toasts the failure.
    } finally {
      setIsResuming(false);
    }
  }, [importerTasks, resumeImportMutation, row.id, showToast]);

  return (
    <WorkspaceImportsListItem
      actions={
        <>
          {showOpen ? (
            <FileStatusButton
              appearance="ghost"
              data-testid="WORKSPACE_IMPORTS_ITEM_OPEN"
              label={openLabel}
              onClick={handleOpen}
            >
              <ExternalLinkIcon aria-hidden size="sm" />
            </FileStatusButton>
          ) : null}
          {row.canResume ? (
            <FileStatusButton
              appearance="ghost"
              data-testid="WORKSPACE_IMPORTS_ITEM_RESUME"
              isLoading={isResuming}
              label="Resume"
              onClick={handleResume}
            >
              <CirclePlayIcon aria-hidden size="sm" />
            </FileStatusButton>
          ) : null}
          {row.canCancel ? (
            <FileStatusButton
              appearance="ghost"
              data-testid="WORKSPACE_IMPORTS_ITEM_CANCEL"
              label="Cancel"
              onClick={handleCancel}
            >
              <CircleXIcon aria-hidden size="sm" />
            </FileStatusButton>
          ) : null}
        </>
      }
      dimmed={isDimmed}
      row={row}
    />
  );
});

WorkspaceImportsRow.displayName = 'WorkspaceImportsRow';
